import React from "react";
import { Box, Container, Heading, Text, VStack, Tabs, TabList, TabPanels, Tab, TabPanel, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon } from "@chakra-ui/react";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const FinanceFAQ: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/finance/faq"
        fallbackTitle="Finance FAQ"
        fallbackDescription="Frequently asked questions about loans and financing"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Text fontSize="sm" color="rgba(255, 255, 255, 0.7)" textTransform="uppercase" letterSpacing="wider">
              One Finance Australia
            </Text>
            <Heading size="2xl" fontWeight="bold">
              Your Finance Questions, Answered
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">Browse common questions by topic, or speak with a specialist for advice specific to your situation.</Text>
          </VStack>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxW="container.xl" py={20} flex="1">
        <VStack spacing={12}>
          {/* Introduction */}
          <Box textAlign="center" maxW="3xl" mx="auto">
            <Heading size="xl" mb={4}>Got Questions? We've Got Answers</Heading>
            <Text fontSize="lg" color="gray.600">
              Browse our frequently asked questions organized by category. Can't find what you're looking for?
              Contact our finance team for personalised assistance.
            </Text>
          </Box>

          {/* FAQ Tabs */}
          <Box w="full">
            <Tabs variant="soft-rounded" colorScheme="blue" isLazy>
              <TabList flexWrap="wrap" justifyContent="center" mb={8}>
                <Tab _selected={{ bg: "blue.500", color: "white" }} fontWeight={600}>Home Loans</Tab>
                <Tab _selected={{ bg: "green.500", color: "white" }} fontWeight={600}>Investment</Tab>
                <Tab _selected={{ bg: "purple.500", color: "white" }} fontWeight={600}>SMSF</Tab>
                <Tab _selected={{ bg: "orange.500", color: "white" }} fontWeight={600}>Refinancing</Tab>
                <Tab _selected={{ bg: "cyan.500", color: "white" }} fontWeight={600}>Asset Finance</Tab>
              </TabList>

              <TabPanels>
                {/* Home Loans Tab */}
                <TabPanel>
                  <Accordion allowToggle>
                    <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="md" mb={4}>
                      <AccordionButton p={5} _hover={{ bg: "blue.50" }}>
                        <Box flex={1} textAlign="left" fontWeight={600}>
                          How much can I borrow for a home loan?
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={5} px={5}>
                        <Text color="gray.700">
                          Your borrowing capacity depends on several factors including your income, expenses, existing debts,
                          credit history, and the property value. Most lenders will allow you to borrow up to 5-6 times your
                          annual income, though this varies. We offer a free borrowing power calculator to give you an estimate.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="md" mb={4}>
                      <AccordionButton p={5} _hover={{ bg: "blue.50" }}>
                        <Box flex={1} textAlign="left" fontWeight={600}>
                          What deposit do I need for a home loan?
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={5} px={5}>
                        <Text color="gray.700">
                          Typically, a 20% deposit is ideal to avoid Lenders Mortgage Insurance (LMI). However, some lenders
                          offer loans with as little as 5% deposit. First home buyers may access government schemes allowing
                          deposits as low as 2-5%. The larger your deposit, the better your loan terms and interest rate.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="md" mb={4}>
                      <AccordionButton p={5} _hover={{ bg: "blue.50" }}>
                        <Box flex={1} textAlign="left" fontWeight={600}>
                          Should I choose a fixed or variable rate?
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={5} px={5}>
                        <Text color="gray.700">
                          Fixed rates provide certainty with locked-in repayments for 1-5 years, protecting you from rate rises.
                          Variable rates fluctuate with the market and often offer more flexibility (offset accounts, extra repayments).
                          Many borrowers choose a split loan (part fixed, part variable) to balance security and flexibility.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="md">
                      <AccordionButton p={5} _hover={{ bg: "blue.50" }}>
                        <Box flex={1} textAlign="left" fontWeight={600}>
                          How long does home loan approval take?
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={5} px={5}>
                        <Text color="gray.700">
                          Pre-approval typically takes 1-3 business days with complete documentation. Full approval after finding
                          a property takes 3-7 days on average. Some lenders offer same-day conditional approval. We work with
                          multiple lenders to find fast approval options for time-sensitive purchases.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </TabPanel>

                {/* Investment Tab */}
                <TabPanel>
                  <Accordion allowToggle>
                    <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="md" mb={4}>
                      <AccordionButton p={5} _hover={{ bg: "green.50" }}>
                        <Box flex={1} textAlign="left" fontWeight={600}>
                          What are the benefits of investment property?
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={5} px={5}>
                        <Text color="gray.700">
                          Investment properties offer multiple benefits: rental income to offset loan costs, capital growth over time,
                          tax deductions on loan interest and expenses, potential negative gearing benefits, and long-term wealth
                          building through equity. They're a proven strategy for building financial security.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="md" mb={4}>
                      <AccordionButton p={5} _hover={{ bg: "green.50" }}>
                        <Box flex={1} textAlign="left" fontWeight={600}>
                          Can I use equity to buy an investment property?
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={5} px={5}>
                        <Text color="gray.700">
                          Yes! If you have sufficient equity in your home (typically need 20% equity plus the deposit for the new property),
                          you can use it as security for an investment property loan. This allows you to invest without needing cash savings.
                          We can help structure this correctly for tax efficiency.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="md">
                      <AccordionButton p={5} _hover={{ bg: "green.50" }}>
                        <Box flex={1} textAlign="left" fontWeight={600}>
                          What is negative gearing?
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={5} px={5}>
                        <Text color="gray.700">
                          Negative gearing occurs when your rental income is less than your property expenses (loan interest, rates, maintenance).
                          The loss can be offset against your taxable income, reducing your tax. While you're losing money short-term,
                          the strategy aims for long-term capital growth to make overall profit.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </TabPanel>

                {/* SMSF Tab */}
                <TabPanel>
                  <Accordion allowToggle>
                    <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="md" mb={4}>
                      <AccordionButton p={5} _hover={{ bg: "purple.50" }}>
                        <Box flex={1} textAlign="left" fontWeight={600}>
                          What is an SMSF and how does it work?
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={5} px={5}>
                        <Text color="gray.700">
                          A Self-Managed Super Fund (SMSF) is a private superannuation fund you manage yourself. You make investment
                          decisions, choose assets (property, shares, etc.), and control your retirement strategy. SMSFs offer greater
                          control and flexibility but require active management and compliance with strict regulations.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="md" mb={4}>
                      <AccordionButton p={5} _hover={{ bg: "purple.50" }}>
                        <Box flex={1} textAlign="left" fontWeight={600}>
                          Can I buy property through my SMSF?
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={5} px={5}>
                        <Text color="gray.700">
                          Yes! You can purchase residential or commercial property through your SMSF using a Limited Recourse Borrowing
                          Arrangement (LRBA). The property must be held for investment purposes (can't live in it) and rental income
                          goes to your super fund. It's taxed at maximum 15%, with potential for tax-free income in pension phase.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="md">
                      <AccordionButton p={5} _hover={{ bg: "purple.50" }}>
                        <Box flex={1} textAlign="left" fontWeight={600}>
                          How much do I need to start an SMSF?
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={5} px={5}>
                        <Text color="gray.700">
                          While you can start with any amount, most experts recommend at least $200,000 to make an SMSF cost-effective
                          due to setup and ongoing compliance costs ($2,000-$5,000 annually). With property investment, you'll need
                          enough for a deposit plus setup costs. We can help assess if an SMSF is right for your situation.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </TabPanel>

                {/* Refinancing Tab */}
                <TabPanel>
                  <Accordion allowToggle>
                    <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="md" mb={4}>
                      <AccordionButton p={5} _hover={{ bg: "orange.50" }}>
                        <Box flex={1} textAlign="left" fontWeight={600}>
                          When should I consider refinancing?
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={5} px={5}>
                        <Text color="gray.700">
                          Consider refinancing if: interest rates have dropped (even 0.5% can save thousands), you want better loan
                          features (offset account, redraw), you're consolidating debt, you need to access equity, or your current
                          rate is higher than new customer rates. Our refinance calculator shows potential savings.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="md" mb={4}>
                      <AccordionButton p={5} _hover={{ bg: "orange.50" }}>
                        <Box flex={1} textAlign="left" fontWeight={600}>
                          What are the costs of refinancing?
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={5} px={5}>
                        <Text color="gray.700">
                          Refinancing costs typically include: break fees on fixed loans (if applicable), discharge fees from current
                          lender ($150-$400), application fees with new lender (often waived), valuation fees ($200-$600), and potential
                          legal fees. These costs should be weighed against long-term savings.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="md">
                      <AccordionButton p={5} _hover={{ bg: "orange.50" }}>
                        <Box flex={1} textAlign="left" fontWeight={600}>
                          How long does refinancing take?
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={5} px={5}>
                        <Text color="gray.700">
                          Refinancing typically takes 2-4 weeks from application to settlement. This includes valuation (3-7 days),
                          loan approval (5-10 days), and settlement preparation. We handle the paperwork and coordination with both
                          lenders to make the process smooth and efficient.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </TabPanel>

                {/* Asset Finance Tab */}
                <TabPanel>
                  <Accordion allowToggle>
                    <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="md" mb={4}>
                      <AccordionButton p={5} _hover={{ bg: "cyan.50" }}>
                        <Box flex={1} textAlign="left" fontWeight={600}>
                          What's the difference between a car loan and chattel mortgage?
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={5} px={5}>
                        <Text color="gray.700">
                          A car loan (consumer loan) is for personal use with simple repayments and no tax benefits. A chattel mortgage
                          is for business use, where you own the vehicle, claim tax deductions on interest and depreciation, and can
                          include a balloon payment. Chattel mortgages typically offer better rates for business purposes.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="md" mb={4}>
                      <AccordionButton p={5} _hover={{ bg: "cyan.50" }}>
                        <Box flex={1} textAlign="left" fontWeight={600}>
                          Can I finance a used vehicle or equipment?
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={5} px={5}>
                        <Text color="gray.700">
                          Yes! Most lenders finance used vehicles and equipment up to 10-15 years old depending on type and condition.
                          Interest rates may be slightly higher than new asset finance, and maximum loan terms may be shorter. The asset
                          must meet minimum value requirements (typically $5,000+).
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>

                    <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="md">
                      <AccordionButton p={5} _hover={{ bg: "cyan.50" }}>
                        <Box flex={1} textAlign="left" fontWeight={600}>
                          What is a balloon payment?
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={5} px={5}>
                        <Text color="gray.700">
                          A balloon payment is a lump sum due at the end of your loan term (typically 20-50% of purchase price).
                          It reduces your monthly repayments during the loan term. At maturity, you can pay the balloon, refinance it,
                          trade in the asset, or sell it. Balloons are common in business asset finance for cash flow management.
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>

          {/* Contact CTA */}
          <Box w="full" bg="blue.50" p={10} borderRadius="xl" textAlign="center" border="1px solid" borderColor="blue.200">
            <Heading size="md" mb={3}>Still Have Questions?</Heading>
            <Text color="gray.600" mb={4} maxW="2xl" mx="auto">
              Our finance specialists are here to help. Get personalized answers to your specific situation.
            </Text>
            <Text fontWeight={600} color={brandConfig.colors.primary}><a href="/finance/contact" style={{ textDecoration: "underline" }}>Book a free consultation with our team</a></Text>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceFAQ;
